import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import { Link } from 'react-router-dom';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Toujours true, obligatoire
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà fait son choix
    const cookieConsent = localStorage.getItem('atomgroup-cookie-consent');
    if (!cookieConsent) {
      // Attendre 2 secondes avant d'afficher la bannière
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 2000);
      return () => clearTimeout(timer);
    } else {
      // Charger les préférences sauvegardées
      const savedPreferences = JSON.parse(cookieConsent);
      setCookiePreferences(savedPreferences);
      initializeTracking(savedPreferences);
    }
  }, []);

  const initializeTracking = (preferences) => {
    // Initialiser Google Analytics si autorisé
    if (preferences.analytics && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': 'granted'
      });
    }

    // Initialiser PostHog si autorisé
    if (preferences.analytics && window.posthog) {
      window.posthog.opt_in_capturing();
    }
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    
    setCookiePreferences(allAccepted);
    localStorage.setItem('atomgroup-cookie-consent', JSON.stringify(allAccepted));
    initializeTracking(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    
    setCookiePreferences(onlyNecessary);
    localStorage.setItem('atomgroup-cookie-consent', JSON.stringify(onlyNecessary));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('atomgroup-cookie-consent', JSON.stringify(cookiePreferences));
    initializeTracking(cookiePreferences);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handlePreferenceChange = (type, value) => {
    setCookiePreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/70 z-40" />
      
      {/* Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gray-900/95 backdrop-blur-xl border-2 border-white/30 rounded-2xl p-6 shadow-2xl">
            {!showSettings ? (
              // Bannière principale
              <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6">
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-xl p-3 flex-shrink-0">
                    <Cookie className="w-full h-full text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-2">
                      🍪 Nous utilisons des cookies
                    </h3>
                    <p className="text-gray-300 text-sm">
                      Nous utilisons des cookies et des technologies similaires pour améliorer votre expérience, 
                      analyser notre trafic et personnaliser le contenu. Vous pouvez choisir d'accepter tous les cookies, 
                      de les refuser ou de personnaliser vos préférences.
                    </p>
                    <p className="text-gray-400 text-xs mt-2">
                      En savoir plus dans notre{' '}
                      <Link to="/confidentialite" className="text-blue-400 hover:text-blue-300 underline">
                        politique de confidentialité
                      </Link>
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 lg:flex-shrink-0">
                  <button
                    onClick={handleRejectAll}
                    className="px-6 py-2 text-sm font-medium text-gray-300 hover:text-white border border-gray-500 hover:border-gray-400 rounded-lg transition-colors"
                  >
                    Refuser tout
                  </button>
                  <button
                    onClick={() => setShowSettings(true)}
                    className="px-6 py-2 text-sm font-medium text-white bg-white/10 hover:bg-white/20 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Settings size={16} />
                    Personnaliser
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="btn-glass btn-primary text-sm px-8 py-2"
                  >
                    Accepter tout
                  </button>
                </div>
              </div>
            ) : (
              // Panneau de paramètres détaillés
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-semibold text-white flex items-center gap-3">
                    <Settings className="w-6 h-6 text-blue-400" />
                    Paramètres des cookies
                  </h3>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-2 text-gray-400 hover:text-white transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Cookies nécessaires */}
                  <div className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-white">Cookies nécessaires</h4>
                      <div className="w-12 h-6 bg-green-500 rounded-full flex items-center justify-end px-1">
                        <div className="w-4 h-4 bg-white rounded-full"></div>
                      </div>
                    </div>
                    <p className="text-sm text-gray-300">
                      Ces cookies sont indispensables au fonctionnement du site web et ne peuvent pas être désactivés.
                      Ils sont généralement définis en réponse à des actions de votre part (connexion, panier, etc.).
                    </p>
                  </div>

                  {/* Cookies analytiques */}
                  <div className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-white">Cookies analytiques</h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={cookiePreferences.analytics}
                          onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                        />
                        <div className={`w-12 h-6 rounded-full transition-colors ${
                          cookiePreferences.analytics ? 'bg-blue-500' : 'bg-gray-600'
                        }`}>
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            cookiePreferences.analytics ? 'translate-x-7' : 'translate-x-1'
                          } mt-1`}></div>
                        </div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-300">
                      Ces cookies nous permettent de compter les visites et sources de trafic, 
                      afin de mesurer et d'améliorer les performances de notre site web.
                    </p>
                  </div>

                  {/* Cookies marketing */}
                  <div className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-white">Cookies marketing</h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={cookiePreferences.marketing}
                          onChange={(e) => handlePreferenceChange('marketing', e.target.checked)}
                        />
                        <div className={`w-12 h-6 rounded-full transition-colors ${
                          cookiePreferences.marketing ? 'bg-blue-500' : 'bg-gray-600'
                        }`}>
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            cookiePreferences.marketing ? 'translate-x-7' : 'translate-x-1'
                          } mt-1`}></div>
                        </div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-300">
                      Ces cookies peuvent être définis par nos partenaires publicitaires pour créer un 
                      profil de vos intérêts et vous montrer des publicités pertinentes.
                    </p>
                  </div>

                  {/* Cookies de préférences */}
                  <div className="p-4 bg-white/5 rounded-xl">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-white">Cookies de préférences</h4>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={cookiePreferences.preferences}
                          onChange={(e) => handlePreferenceChange('preferences', e.target.checked)}
                        />
                        <div className={`w-12 h-6 rounded-full transition-colors ${
                          cookiePreferences.preferences ? 'bg-blue-500' : 'bg-gray-600'
                        }`}>
                          <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                            cookiePreferences.preferences ? 'translate-x-7' : 'translate-x-1'
                          } mt-1`}></div>
                        </div>
                      </label>
                    </div>
                    <p className="text-sm text-gray-300">
                      Ces cookies permettent au site web de mémoriser les choix que vous faites pour vous 
                      fournir des fonctionnalités améliorées et un contenu plus personnalisé.
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-3 mt-8">
                  <button
                    onClick={handleRejectAll}
                    className="px-6 py-3 text-sm font-medium text-gray-300 hover:text-white border border-gray-500 hover:border-gray-400 rounded-lg transition-colors"
                  >
                    Refuser tout
                  </button>
                  <button
                    onClick={handleSavePreferences}
                    className="btn-glass btn-primary text-sm px-8 py-3"
                  >
                    Enregistrer mes préférences
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="btn-glass text-sm px-8 py-3"
                  >
                    Accepter tout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieBanner;